import { useQuery } from '@tanstack/react-query'
import http from '@/lib/http'
import { queryClient } from '@/services/queryClient'
import { useFlag } from '@unleash/proxy-client-react'
import { useMedicationRequestById } from '@/services/medicationRequests'
import { jsonSafeParse } from '@/utils/jsonSafeParse'

interface UseScrAiSummaryProps {
  medicationRequestId: string
  enabled?: boolean
}

export const useScrAiSummary = ({ medicationRequestId, enabled = true }: UseScrAiSummaryProps) => {
  const { data: medRequest } = useMedicationRequestById(medicationRequestId, enabled)
  const isMedRequestValidForSummary =
    medRequest?.patient?.scrData && medRequest.patient.scrData.length > 5 && !!medRequest?.orderMedNameString
  const shouldGetScrSummary = useFlag('scr-summary')

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: ['scrAiSummary', medicationRequestId]
    })
  }

  const query = useQuery({
    queryKey: ['scrAiSummary', medicationRequestId],
    queryFn: async () => {
      if (!isMedRequestValidForSummary) return

      const { prompt, summary } = (await http.post(
        `/prescriber/scr-ai/summary`,
        {
          scrData: medRequest.patient.scrData,
          orderMedNameString: medRequest.orderMedNameString,
          orderedItemCategory: medRequest.orderedItem?.category ?? 'Unknown category'
        },
        {
          timeout: 45000
        }
      )) as {
        prompt: string
        summary: string
      }

      const [mainText, attentionText] = summary.split('<PATIENT_FLAGS_MAY_NEED_ATTENTION>')

      return {
        attentionText: attentionText
          .replace('<PATIENT_FLAGS_MAY_NEED_ATTENTION>', '')
          .replace('</PATIENT_FLAGS_MAY_NEED_ATTENTION>', '')
          .split('\n'),
        mainText: mainText.split('\n'),
        prompt: prompt,
        summary: summary
      }
    },
    enabled:
      !!(enabled && shouldGetScrSummary && isMedRequestValidForSummary && !medRequest?.OTC) &&
      !!jsonSafeParse(medRequest.patient.scrData) &&
      !!medRequest.patient.scrData &&
      medRequest.patient.scrData?.length < 50000
  })

  return {
    ...query,
    mainText: query.data?.mainText ?? [],
    attentionText: query.data?.attentionText ?? [],
    summary: query.data?.summary ?? '',
    prompt: query.data?.prompt ?? '',
    invalidate
  }
}
